import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DieselLayout } from '../../components/diesel/DieselLayout';
import { useDiesel } from '../../context/DieselContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
    Truck,
    Plus,
    Search,
    Eye,
    Pencil,
    Trash2,
    RefreshCw,
    Fuel,
    Gauge,
    AlertTriangle,
    Filter,
} from 'lucide-react';
import { ImportarExcel } from '../../components/diesel/ImportarExcel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DieselVehiculosPage() {
    const { hasPermission } = useDiesel();
    const [vehiculos, setVehiculos] = useState([]);
    const [modelos, setModelos] = useState([]);
    const [conductores, setConductores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [estadoFilter, setEstadoFilter] = useState('activo');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogBaja, setDialogBaja] = useState({ open: false, vehiculo: null });
    const [formData, setFormData] = useState({});
    const [saving, setSaving] = useState(false);

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => {
        loadData();
    }, [estadoFilter]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [vRes, mRes, cRes] = await Promise.all([
                fetch(`${API_URL}/api/diesel/vehiculos?estado=${estadoFilter}`, { headers }),
                fetch(`${API_URL}/api/diesel/modelos`, { headers }),
                fetch(`${API_URL}/api/diesel/conductores?estado=activo`, { headers }),
            ]);

            if (vRes.ok) setVehiculos(await vRes.json());
            if (mRes.ok) setModelos(await mRes.json());
            if (cRes.ok) setConductores(await cRes.json());
        } catch (err) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const filteredVehiculos = vehiculos.filter(v =>
        v.patente?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.modelo_info?.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.modelo_info?.marca?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSave = async () => {
        if (!formData.patente || !formData.modelo_id) {
            toast.error('Complete los campos obligatorios');
            return;
        }

        setSaving(true);
        try {
            const method = formData.id ? 'PUT' : 'POST';
            const url = formData.id 
                ? `${API_URL}/api/diesel/vehiculos/${formData.id}`
                : `${API_URL}/api/diesel/vehiculos`;

            const res = await fetch(url, { method, headers, body: JSON.stringify(formData) });

            if (res.ok) {
                toast.success(formData.id ? 'Vehículo actualizado' : 'Vehículo creado');
                setDialogOpen(false);
                setFormData({});
                loadData();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error al guardar');
            }
        } catch (err) {
            toast.error('Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleBaja = async () => {
        try {
            const res = await fetch(`${API_URL}/api/diesel/vehiculos/${dialogBaja.vehiculo.id}/baja`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ motivo: dialogBaja.motivo }),
            });

            if (res.ok) {
                toast.success('Vehículo dado de baja');
                setDialogBaja({ open: false, vehiculo: null });
                loadData();
            }
        } catch (err) {
            toast.error('Error al dar de baja');
        }
    };

    return (
        <DieselLayout>
            <div className="space-y-6 animate-fade-in">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight">
                            Vehículos
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            {filteredVehiculos.length} vehículo{filteredVehiculos.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        {hasPermission('crear_vehiculos') && (
                            <>
                                <ImportarExcel tipo="vehiculos" label="Vehiculos" onSuccess={loadData} />
                                <Button
                                    onClick={() => { setFormData({}); setDialogOpen(true); }}
                                    className="bg-[var(--diesel-primary)] text-[var(--diesel-secondary)]"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Nuevo Vehiculo
                                </Button>
                            </>
                        )}
                        <Button variant="outline" onClick={loadData}>
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/* Filtros */}
                <Card className="border-2">
                    <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Buscar por patente, marca o modelo..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                            <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                                <SelectTrigger className="w-40">
                                    <Filter className="h-4 w-4 mr-2" />
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="activo">Activos</SelectItem>
                                    <SelectItem value="baja">Dados de baja</SelectItem>
                                    <SelectItem value="todos">Todos</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Tabla */}
                <Card className="border-2">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Patente</TableHead>
                                <TableHead>Marca / Modelo</TableHead>
                                <TableHead>Conductor</TableHead>
                                <TableHead>Última Carga</TableHead>
                                <TableHead>Rendimiento</TableHead>
                                <TableHead>Estado</TableHead>
                                <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                                        <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                                        <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                                        <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                                        <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                                        <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                                        <TableCell><Skeleton className="h-8 w-20 ml-auto" /></TableCell>
                                    </TableRow>
                                ))
                            ) : filteredVehiculos.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <div className="py-12 text-center">
                                            <Truck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                            <p className="font-medium">Sin vehículos</p>
                                            <p className="text-sm text-muted-foreground">
                                                {searchTerm ? 'No se encontraron resultados' : 'Registre el primer vehículo'}
                                            </p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredVehiculos.map((v) => (
                                    <TableRow key={v.id}>
                                        <TableCell>
                                            <span className="font-mono font-bold text-lg">{v.patente}</span>
                                        </TableCell>
                                        <TableCell>
                                            <div>
                                                <p className="font-medium">{v.modelo_info?.marca} {v.modelo_info?.nombre}</p>
                                                {v.año && <p className="text-xs text-muted-foreground">{v.año}</p>}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {v.conductores_info?.length > 0 ? (
                                                <div className="space-y-1">
                                                    {v.conductores_info.map((c, i) => (
                                                        <p key={i} className="text-sm">{c.nombre}</p>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {v.ultima_carga ? (
                                                <div className="text-sm">
                                                    <p>{v.ultima_carga.litros}L</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {new Date(v.ultima_carga.fecha).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {v.rendimiento_promedio ? (
                                                <div className="flex items-center gap-1">
                                                    <Gauge className="h-4 w-4" style={{ color: 'var(--diesel-primary)' }} />
                                                    <span className="font-mono">{v.rendimiento_promedio} km/l</span>
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={v.estado === 'activo' ? 'default' : 'secondary'}>
                                                {v.estado}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                <Link to={`/diesel/vehiculos/${v.id}`}>
                                                    <Button variant="ghost" size="icon">
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                </Link>
                                                {hasPermission('editar_vehiculos') && v.estado === 'activo' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => { setFormData(v); setDialogOpen(true); }}
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                {hasPermission('dar_baja_vehiculos') && v.estado === 'activo' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => setDialogBaja({ open: true, vehiculo: v })}
                                                    >
                                                        <Trash2 className="h-4 w-4 text-destructive" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Card>
            </div>

            {/* Dialog Crear/Editar */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{formData.id ? 'Editar' : 'Nuevo'} Vehículo</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Patente *</Label>
                                <Input
                                    value={formData.patente || ''}
                                    onChange={(e) => setFormData({ ...formData, patente: e.target.value.toUpperCase() })}
                                    placeholder="XXXX00"
                                    disabled={!!formData.id}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Año</Label>
                                <Input
                                    type="number"
                                    value={formData.año || ''}
                                    onChange={(e) => setFormData({ ...formData, año: parseInt(e.target.value) })}
                                    placeholder="2024"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Modelo *</Label>
                            <select
                                className="w-full p-2 border rounded-md bg-background"
                                value={formData.modelo_id || ''}
                                onChange={(e) => setFormData({ ...formData, modelo_id: e.target.value })}
                            >
                                <option value="">Seleccionar modelo</option>
                                {modelos.map((m) => (
                                    <option key={m.id} value={m.id}>
                                        {m.marca_nombre} - {m.nombre} ({m.rendimiento_fabricante} km/l)
                                    </option>
                                ))}
                            </select>
                            {modelos.length === 0 && (
                                <p className="text-xs text-amber-600">Primero registre marcas y modelos en Configuración</p>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Color</Label>
                                <Input
                                    value={formData.color || ''}
                                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    placeholder="Blanco"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Capacidad Estanque (L)</Label>
                                <Input
                                    type="number"
                                    value={formData.capacidad_estanque || ''}
                                    onChange={(e) => setFormData({ ...formData, capacidad_estanque: parseFloat(e.target.value) })}
                                    placeholder="400"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Conductor 1</Label>
                            <select
                                className="w-full p-2 border rounded-md bg-background"
                                value={formData.conductores_asignados?.[0] || ''}
                                onChange={(e) => {
                                    const arr = [...(formData.conductores_asignados || [])];
                                    arr[0] = e.target.value || null;
                                    setFormData({ ...formData, conductores_asignados: arr.filter(Boolean) });
                                }}
                            >
                                <option value="">Sin conductor</option>
                                {conductores.map((c) => (
                                    <option key={c.id} value={c.id}>{c.nombre} ({c.rut})</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <Label>Conductor 2</Label>
                            <select
                                className="w-full p-2 border rounded-md bg-background"
                                value={formData.conductores_asignados?.[1] || ''}
                                onChange={(e) => {
                                    const arr = [...(formData.conductores_asignados || [])];
                                    if (arr.length === 0) arr.push(null);
                                    arr[1] = e.target.value || null;
                                    setFormData({ ...formData, conductores_asignados: arr.filter(Boolean) });
                                }}
                            >
                                <option value="">Sin conductor</option>
                                {conductores.map((c) => (
                                    <option key={c.id} value={c.id}>{c.nombre} ({c.rut})</option>
                                ))}
                            </select>
                        </div>

                        <div className="space-y-2">
                            <Label>Umbral de Alerta Personalizado (%)</Label>
                            <Input
                                type="number"
                                value={formData.umbral_alerta || ''}
                                onChange={(e) => setFormData({ ...formData, umbral_alerta: e.target.value ? parseFloat(e.target.value) : null })}
                                placeholder="Usar valor del modelo o global"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Observaciones</Label>
                            <Input
                                value={formData.observaciones || ''}
                                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? 'Guardando...' : 'Guardar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog Dar de Baja */}
            <Dialog open={dialogBaja.open} onOpenChange={(open) => !open && setDialogBaja({ open: false, vehiculo: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-destructive">
                            <AlertTriangle className="h-5 w-5" />
                            Dar de Baja Vehículo
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="mb-4">
                            ¿Está seguro de dar de baja el vehículo <strong>{dialogBaja.vehiculo?.patente}</strong>?
                        </p>
                        <p className="text-sm text-muted-foreground mb-4">
                            El historial de cargas se mantendrá para consultas.
                        </p>
                        <div className="space-y-2">
                            <Label>Motivo de la baja</Label>
                            <Input
                                value={dialogBaja.motivo || ''}
                                onChange={(e) => setDialogBaja({ ...dialogBaja, motivo: e.target.value })}
                                placeholder="Ej: Venta, siniestro, fin de vida útil..."
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogBaja({ open: false, vehiculo: null })}>
                            Cancelar
                        </Button>
                        <Button variant="destructive" onClick={handleBaja}>
                            Confirmar Baja
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DieselLayout>
    );
}
